import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
  type DocumentReference,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from '../firebase'
import {
  setHelperWorkspaces,
  type HelperWorkspaceSummary,
} from './helperStore'
import { DEBUG_TEAM_WORKSPACE, isNoSignInPreviewActive } from '../../debug'
import { normalizeChecklistBlockedTodoPolicy } from '../../utils/checklistBlockedTodoPolicy'
import type { NlsLocale } from '../../nls/nlsTypes'
import { normalizeHelperAppLocale } from './languageRegistryService'
import type { HelperVisibilityMask } from './visibilityMask'
import type { HelperMutationRecord } from './helperOutboxDb'
import type { HelperAssignedItem } from './helperAssignedItemsService'

export interface ResolveFocusedChatItemOptions {
  chatActionItemId: string | null
  detailsItem: HelperAssignedItem | null
  ghostFocusItemId: string | null
  activeWorkspaceId: string | null
  items: HelperAssignedItem[]
}

export const resolveFocusedChatItem = ({
  chatActionItemId,
  detailsItem,
  ghostFocusItemId,
  activeWorkspaceId,
  items,
}: ResolveFocusedChatItemOptions): HelperAssignedItem | null => {
  if (chatActionItemId) {
    const found = items.find((item) => item.id === chatActionItemId)
    if (found && found.workspaceId === activeWorkspaceId) return found
  }
  if (detailsItem && detailsItem.workspaceId === activeWorkspaceId) {
    return detailsItem
  }
  if (ghostFocusItemId) {
    const found = items.find((item) => item.id === ghostFocusItemId)
    if (found && found.workspaceId === activeWorkspaceId) return found
  }
  return items.find((item) => item.workspaceId === activeWorkspaceId) ?? null
}

export const pendingCountFor = (workspaceId: string, pending: HelperMutationRecord[]): number => (
  pending.reduce((count, mutation) => {
    const payload = mutation.payload
    if (payload && typeof payload === 'object' && 'workspaceId' in payload && payload.workspaceId === workspaceId) {
      return count + 1
    }
    return count
  }, 0)
)

const toIso = (value: unknown): string | null => {
  if (!value) return null
  if (typeof value === 'string') return value
  if (typeof value === 'object' && 'toDate' in (value as Record<string, unknown>)) {
    const fn = (value as { toDate?: () => Date }).toDate
    if (typeof fn === 'function') {
      try {
        return fn.call(value).toISOString()
      } catch {
        return null
      }
    }
  }
  return null
}

const PALETTE = ['#6366f1', '#22d3ee', '#f59e0b', '#ec4899', '#84cc16', '#a855f7', '#10b981', '#ef4444'] // mosaic-keep: data (workspace initial accent color palette for WorkspaceBrandChip)

export const deriveWorkspaceColor = (workspaceId: string): string => {
  let hash = 0
  for (let i = 0; i < workspaceId.length; i += 1) {
    hash = (hash * 31 + workspaceId.charCodeAt(i)) | 0
  }
  return PALETTE[Math.abs(hash) % PALETTE.length]
}

export const deriveWorkspaceInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

type MembershipRecord = {
  workspaceId: string
  workspaceName?: string | null
  role?: string | null
  joinedAt?: string | null
  helperAppLocale?: NlsLocale | null
  appAccess?: boolean
  notifyVia?: 'BOTH' | 'APP_ONLY' | 'WHATSAPP_ONLY'
}

const readHelperAppLocale = (value: unknown): NlsLocale | null =>
  typeof value === 'string' && value.trim() ? normalizeHelperAppLocale(value) : null

export const collectHelperMembershipRecords = (docs: DocumentData[]): Map<string, MembershipRecord> => {
  const mergedMap = new Map<string, MembershipRecord>()
  for (const data of docs) {
    const workspaceId = typeof data.workspaceId === 'string' ? data.workspaceId : null
    if (!workspaceId) continue
    const status = typeof data.status === 'string' ? data.status : 'ACTIVE'
    if (status !== 'ACTIVE') continue
    const helperAppLocale = readHelperAppLocale(data.helperAppLocale)
    const role = typeof data.role === 'string' ? data.role : null
    const joinedAt = toIso(data.respondedAt) ?? toIso(data.invitedAt)
    const appAccess = typeof data.appAccess === 'boolean' ? data.appAccess : undefined
    const notifyVia = typeof data.notifyVia === 'string' && ['BOTH', 'APP_ONLY', 'WHATSAPP_ONLY'].includes(data.notifyVia)
      ? (data.notifyVia as 'BOTH' | 'APP_ONLY' | 'WHATSAPP_ONLY')
      : undefined
    const workspaceName = typeof data.workspaceName === 'string' && data.workspaceName.trim() ? data.workspaceName.trim() : null

    const existing = mergedMap.get(workspaceId)
    if (existing) {
      if (!existing.role && role) existing.role = role
      if (!existing.helperAppLocale && helperAppLocale) existing.helperAppLocale = helperAppLocale
      if (!existing.joinedAt && joinedAt) existing.joinedAt = joinedAt
      if (!existing.workspaceName && workspaceName) existing.workspaceName = workspaceName
      if (existing.appAccess === undefined && appAccess !== undefined) existing.appAccess = appAccess
      if (existing.notifyVia === undefined && notifyVia !== undefined) existing.notifyVia = notifyVia
      continue
    }
    mergedMap.set(workspaceId, {
      workspaceId,
      workspaceName,
      role,
      joinedAt,
      helperAppLocale,
      appAccess,
      notifyVia,
    })
  }

  const helperOnlyMap = new Map<string, MembershipRecord>()
  for (const [wsId, record] of mergedMap.entries()) {
    if (record.role === 'HELPER') {
      helperOnlyMap.set(wsId, record)
    }
  }
  return helperOnlyMap
}

export const hydrateWorkspaceSummary = async (
  membership: MembershipRecord,
): Promise<HelperWorkspaceSummary | null> => {
  if (membership.appAccess === false) {
    return {
      id: membership.workspaceId,
      name: membership.workspaceName?.trim() || membership.workspaceId,
      avatarUrl: null,
      color: deriveWorkspaceColor(membership.workspaceId),
      role: membership.role ?? null,
      joinedAt: membership.joinedAt ?? null,
      helperAppLocale: membership.helperAppLocale ?? null,
      cardBackgroundsEnabled: false,
      checklistBlockedTodoPolicy: normalizeChecklistBlockedTodoPolicy(),
      branding: undefined,
      helperVisibilityMaskDefault: null,
      appAccess: false,
      notifyVia: membership.notifyVia,
    }
  }
  try {
    const snapshot = await getDoc(doc(firestoreDb, 'workspaces', membership.workspaceId))
    if (!snapshot.exists()) return null
    const data = snapshot.data() as Record<string, unknown>
    const name = typeof data.name === 'string' && data.name.trim().length > 0
      ? data.name
      : membership.workspaceName?.trim() || membership.workspaceId
    const brandingRaw = data.branding as { logoUrl?: unknown; accentColor?: unknown } | undefined
    const branding = brandingRaw
      ? {
          logoUrl: typeof brandingRaw.logoUrl === 'string' ? brandingRaw.logoUrl : null,
          accentColor: typeof brandingRaw.accentColor === 'string' ? brandingRaw.accentColor : null,
        }
      : undefined
    return {
      id: membership.workspaceId,
      name,
      avatarUrl: null,
      color: deriveWorkspaceColor(membership.workspaceId),
      role: membership.role ?? null,
      joinedAt: membership.joinedAt ?? null,
      helperAppLocale: membership.helperAppLocale ?? null,
      cardBackgroundsEnabled: (data.cardBackgroundSettings as { enabled?: boolean } | undefined)?.enabled !== false
        && (data.workspacePreferences as { cardBackgroundsEnabled?: boolean } | undefined)?.cardBackgroundsEnabled !== false,
      checklistBlockedTodoPolicy: normalizeChecklistBlockedTodoPolicy(data),
      branding,
      helperVisibilityMaskDefault: data.helperVisibilityMaskDefault && typeof data.helperVisibilityMaskDefault === 'object'
        ? (data.helperVisibilityMaskDefault as HelperVisibilityMask)
        : null,
      appAccess: membership.appAccess,
      notifyVia: membership.notifyVia,
    }
  } catch {
    return null
  }
}

/**
 * Subscribes to the helper's workspace memberships and pushes hydrated
 * workspace summaries into helperStore. Phone-auth helper accounts do not
 * carry an email claim, so the self-readable `users/{uid}/workspaceMemberships`
 * mirror is the primary source; the flat collection queries remain legacy
 * fallbacks for older membership rows.
 */
export const subscribeToHelperWorkspaces = (uid: string): Unsubscribe => {
  // Debug bypass: seed the synthetic team workspace so the helper shell
  // (tasks, chat, etc.) renders with data instead of the empty state. No
  // Firestore subscription is set up; nothing to unsubscribe.
  if (isNoSignInPreviewActive()) {
    setHelperWorkspaces([{
      id: DEBUG_TEAM_WORKSPACE.id,
      name: DEBUG_TEAM_WORKSPACE.name,
      avatarUrl: null,
      color: deriveWorkspaceColor(DEBUG_TEAM_WORKSPACE.id),
      role: 'HELPER',
      joinedAt: new Date().toISOString(),
      helperAppLocale: 'en',
      cardBackgroundsEnabled: true,
      checklistBlockedTodoPolicy: normalizeChecklistBlockedTodoPolicy(),
    }], { loading: false })
    return () => {}
  }

  const mirroredMemberships = collection(firestoreDb, 'users', uid, 'workspaceMemberships')
  const membersCollection = collection(firestoreDb, 'workspace_members')
  const queries = [
    query(mirroredMemberships),
    query(membersCollection, where('uid', '==', uid)),
    query(membersCollection, where('userId', '==', uid)),
  ]

  setHelperWorkspaces([], { loading: true })

  const latest: DocumentData[][] = [[], [], []]
  let scheduled = false

  const recompute = async (): Promise<void> => {
    scheduled = false
    const merged = collectHelperMembershipRecords(latest.flat())
    if (merged.size === 0) {
      setHelperWorkspaces([], { loading: false })
      return
    }
    const hydrated = await Promise.all(
      Array.from(merged.values()).map((membership) => hydrateWorkspaceSummary(membership)),
    )
    const summaries = hydrated
      .filter((entry): entry is HelperWorkspaceSummary => entry !== null)
      .sort((a, b) => a.name.localeCompare(b.name))
    setHelperWorkspaces(summaries, { loading: false })
  }

  const scheduleRecompute = (): void => {
    if (scheduled) return
    scheduled = true
    queueMicrotask(() => {
      void recompute()
    })
  }

  const unsubs = queries.map((q, index) =>
    onSnapshot(
      q,
      (snapshot) => {
        latest[index] = snapshot.docs.map((entry) => entry.data())
        scheduleRecompute()
      },
      () => {
        latest[index] = []
        scheduleRecompute()
      },
    ),
  )

  return () => {
    for (const unsub of unsubs) unsub()
  }
}

export const updateHelperWorkspaceAppLocale = async (input: {
  workspaceId: string
  uid: string
  locale: string
}): Promise<NlsLocale> => {
  const locale = normalizeHelperAppLocale(input.locale)
  const membersCollection = collection(firestoreDb, 'workspace_members')
  const snapshots = await Promise.all([
    getDocs(query(membersCollection, where('uid', '==', input.uid))),
    getDocs(query(membersCollection, where('userId', '==', input.uid))),
  ])
  const refs = new Map<string, DocumentReference<DocumentData>>()
  for (const snapshot of snapshots) {
    for (const entry of snapshot.docs) {
      const data = entry.data()
      const workspaceId = typeof data.workspaceId === 'string' ? data.workspaceId : null
      const status = typeof data.status === 'string' ? data.status : 'ACTIVE'
      if (workspaceId !== input.workspaceId || status !== 'ACTIVE') continue
      refs.set(entry.ref.path, entry.ref)
    }
  }
  if (refs.size === 0) throw new Error('Could not find the active helper membership for this workspace.')
  await Promise.all(Array.from(refs.values()).map((ref) => updateDoc(ref, {
    helperAppLocale: locale,
    helperAppLocaleUpdatedAt: serverTimestamp(),
    helperAppLocaleUpdatedBy: input.uid,
  })))
  return locale
}
